import { useState, useCallback, useMemo } from 'react'
import { Accordion, Text, useDebouncedState } from '@retailer-platform/shared-components'
import { TextField, Button, SearchInput } from '@retailer-platform/shared-components/src/tds'
import styled from '@emotion/styled'
import { useLookupAccountForImpersonation } from '../../../../utils/impersonation/useLookupAccountForImpersonation.hooks'
import { useDashMessages } from '../../../../intl/intl.hooks'
import { ImpersonationConfirmationModal } from './impersonation/ImpersonationConfirmationModal'

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
})

const Section = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})

const OrDivider = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  margin: '8px 0',
})

const DividerLine = styled.div({
  flex: 1,
  height: '1px',
  backgroundColor: '#e5e7eb',
})

const AccountOption = styled.div({
  padding: '8px 12px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#f3f4f6',
  },
})

const SelectedAccountBox = styled.div({
  padding: '12px',
  backgroundColor: '#f9fafb',
  border: '1px solid #e5e7eb',
  borderRadius: '4px',
  fontSize: '14px',
})

const ResultsDropdown = styled.div({
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '4px',
  backgroundColor: 'white',
  border: '1px solid #e5e7eb',
  borderRadius: '4px',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 10,
})

interface SelectedAccount {
  canonicalId: string
  givenName?: string
  surname?: string
  email?: string
  canImpersonate: boolean
  impersonateFailureReason?: string | null
}

export const ImpersonateUser = () => {
  const [emailSearchTerm, debouncedEmailTerm, setEmailSearchTerm] = useDebouncedState('', 350)
  const [canonicalIdInput, setCanonicalIdInput] = useState('')
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [showEmailResults, setShowEmailResults] = useState(false)

  const i18n = useDashMessages({
    title: 'app.admin.controlPanel.impersonation.title',
    emailSearchLabel: 'app.admin.controlPanel.impersonation.emailSearch.label',
    emailSearchPlaceholder: 'app.admin.controlPanel.impersonation.emailSearch.placeholder',
    emailSearchNoResults: 'app.admin.controlPanel.impersonation.emailSearch.noResults',
    canonicalIdLabel: 'app.admin.controlPanel.impersonation.canonicalId.label',
    canonicalIdPlaceholder: 'app.admin.controlPanel.impersonation.canonicalId.placeholder',
    canonicalIdInvalid: 'app.admin.controlPanel.impersonation.canonicalId.invalid',
    canonicalIdLookingUp: 'app.admin.controlPanel.impersonation.canonicalId.lookingUp',
    dividerOr: 'app.admin.controlPanel.impersonation.divider.or',
    selectedAccountLabel: 'app.admin.controlPanel.impersonation.selectedAccount.label',
    selectedAccountUuid: 'app.admin.controlPanel.impersonation.selectedAccount.uuid',
    startImpersonationButton: 'app.admin.controlPanel.impersonation.button.startImpersonation',
  })

  // Email search query
  const { data: emailSearchData, loading: emailSearchLoading } = useLookupAccountForImpersonation({
    variables: {
      email: debouncedEmailTerm,
      limit: 10,
    },
    skip: !debouncedEmailTerm || debouncedEmailTerm.length < 3,
  })

  // Canonical ID lookup query
  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(uuid)
  }

  const { data: canonicalIdData, loading: canonicalIdLoading } = useLookupAccountForImpersonation({
    variables: {
      canonicalIds: [canonicalIdInput],
      limit: 1,
    },
    skip: !canonicalIdInput || !isValidUUID(canonicalIdInput),
  })

  // Email search results
  const emailSearchResults = useMemo(
    () => emailSearchData?.enterpriseAccountSearch?.edges?.map(edge => edge.node) ?? [],
    [emailSearchData]
  )

  // Auto-select account from canonical ID lookup
  useMemo(() => {
    if (canonicalIdData?.enterpriseAccountSearch?.edges?.[0]) {
      const account = canonicalIdData.enterpriseAccountSearch.edges[0].node
      setSelectedAccount({
        canonicalId: account.canonicalId,
        givenName: account.givenName,
        surname: account.surname,
        email: account.email,
        canImpersonate: account.canImpersonate,
        impersonateFailureReason: account.impersonateFailureReason,
      })
    }
  }, [canonicalIdData])

  const handleEmailSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value
      setEmailSearchTerm(value)
      setShowEmailResults(value.length >= 3)
      // Clear canonical ID when user searches by email
      if (value && canonicalIdInput) {
        setCanonicalIdInput('')
      }
    },
    [setEmailSearchTerm, canonicalIdInput]
  )

  const handleCanonicalIdChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim()
      setCanonicalIdInput(value)
      // Clear email search when user enters canonical ID
      if (value && emailSearchTerm) {
        setEmailSearchTerm('', true)
        setShowEmailResults(false)
      }
    },
    [emailSearchTerm, setEmailSearchTerm]
  )

  const handleSelectAccount = useCallback(
    (account: SelectedAccount) => {
      setSelectedAccount(account)
      setShowEmailResults(false)
      setEmailSearchTerm('', true)
      setCanonicalIdInput('')
    },
    [setEmailSearchTerm]
  )

  const handleStartImpersonation = () => {
    if (selectedAccount) {
      setIsConfirmModalOpen(true)
    }
  }

  const handleConfirmModalClose = () => {
    setIsConfirmModalOpen(false)
  }

  const handleImpersonationSuccess = () => {
    setIsConfirmModalOpen(false)
    setSelectedAccount(null)
    setEmailSearchTerm('', true)
    setCanonicalIdInput('')
  }

  return (
    <Accordion showContentBackground={true} title={i18n.title}>
      <Container>
        {/* Email Search Section */}
        <Section>
          <Text size="small" css={{ fontWeight: 600 }}>
            {i18n.emailSearchLabel}
          </Text>
          <div style={{ position: 'relative' }}>
            <SearchInput
              placeholder={i18n.emailSearchPlaceholder}
              value={emailSearchTerm}
              onChange={handleEmailSearchChange}
              isLoading={emailSearchLoading}
              onClear={() => {
                setEmailSearchTerm('', true)
                setShowEmailResults(false)
              }}
              width={400}
            />
            {showEmailResults && emailSearchResults.length > 0 && (
              <ResultsDropdown>
                {emailSearchResults.map(account => (
                  <AccountOption
                    key={account.canonicalId}
                    onClick={() => handleSelectAccount(account)}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {account.givenName} {account.surname}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>{account.email}</div>
                  </AccountOption>
                ))}
              </ResultsDropdown>
            )}
            {showEmailResults &&
              !emailSearchLoading &&
              emailSearchResults.length === 0 &&
              debouncedEmailTerm.length >= 3 && (
                <ResultsDropdown>
                  <Text size="small" css={{ padding: '12px', color: '#6b7280' }}>
                    {i18n.emailSearchNoResults}
                  </Text>
                </ResultsDropdown>
              )}
          </div>
        </Section>

        {/* OR Divider */}
        <OrDivider>
          <DividerLine />
          <Text size="small" css={{ color: '#6b7280' }}>
            {i18n.dividerOr}
          </Text>
          <DividerLine />
        </OrDivider>

        {/* Canonical ID Section */}
        <Section>
          <Text size="small" css={{ fontWeight: 600 }}>
            {i18n.canonicalIdLabel}
          </Text>
          <TextField
            placeholder={i18n.canonicalIdPlaceholder}
            value={canonicalIdInput}
            onChange={handleCanonicalIdChange}
            error={canonicalIdInput && !isValidUUID(canonicalIdInput)}
          />
          {canonicalIdInput && !isValidUUID(canonicalIdInput) && (
            <Text size="small" css={{ color: '#dc2626' }}>
              {i18n.canonicalIdInvalid}
            </Text>
          )}
          {canonicalIdLoading && (
            <Text size="small" css={{ color: '#6b7280' }}>
              {i18n.canonicalIdLookingUp}
            </Text>
          )}
        </Section>

        {/* Selected Account Display */}
        {selectedAccount && (
          <Section>
            <Text size="small" css={{ fontWeight: 600 }}>
              {i18n.selectedAccountLabel}
            </Text>
            <SelectedAccountBox>
              <div style={{ marginBottom: '4px', fontWeight: 500 }}>
                {selectedAccount.givenName} {selectedAccount.surname}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                {selectedAccount.email}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {i18n.selectedAccountUuid} {selectedAccount.canonicalId}
              </div>
            </SelectedAccountBox>
          </Section>
        )}

        {/* Start Impersonation Button */}
        <Button
          variant="primary"
          onClick={handleStartImpersonation}
          disabled={!selectedAccount}
          style={{ width: 'fit-content' }}
        >
          {i18n.startImpersonationButton}
        </Button>
      </Container>

      {/* Confirmation Modal */}
      <ImpersonationConfirmationModal
        isOpen={isConfirmModalOpen}
        targetAccount={selectedAccount}
        onCancel={handleConfirmModalClose}
        onSuccess={handleImpersonationSuccess}
      />
    </Accordion>
  )
}
